import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

function clp(n) {
  return `$${Number(n || 0).toLocaleString('es-CL')}`;
}

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL');
}

export default function IngresoDetalleDrawer({
  cid,
  row,
  periodoServicioLabel,
  onClose,
  onRegistrarPago,
  onRefresh
}) {
  const [editCargoId, setEditCargoId] = useState(null);
  const [saldoObjetivo, setSaldoObjetivo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [pagos, setPagos] = useState([]);

  const loadPagos = useCallback(() => {
    if (!cid || !row?.id) return Promise.resolve();
    return api
      .get(`/gastos-comunes/condominios/${cid}/pagos`, { params: { unidadId: row.id, limit: 20 } })
      .then((r) => setPagos(r.data || []))
      .catch(() => setPagos([]));
  }, [cid, row?.id]);

  useEffect(() => {
    setError('');
    setOkMsg('');
    setEditCargoId(null);
    loadPagos();
  }, [row?.id, loadPagos]);

  if (!row) return null;

  const hayDuplicados = row.mesesImpagos?.some((m) => m.hayDuplicadoEnPeriodo);

  const tieneAbono = (m) => Number(m.aplicadoMonto || 0) > 0;

  const refrescarTodo = async () => {
    await loadPagos();
    if (onRefresh) await onRefresh();
  };

  const anularCargo = async (cargo) => {
    if (!cargo?.cargoId) {
      setError('Cargo sin identificador; recargue la página.');
      return;
    }
    if (tieneAbono(cargo)) {
      setError('Este cargo tiene abono. Revierta el pago abajo o use «Quitar duplicados sin abono».');
      return;
    }
    const ok = window.confirm(
      `¿Anular el cargo de ${cargo.label}${cargo.saldoCero ? ' (saldo 0)' : ` (${clp(cargo.pendiente)})`}?`
    );
    if (!ok) return;
    setError('');
    setOkMsg('');
    setBusy(true);
    try {
      await api.post(`/gastos-comunes/condominios/${cid}/unidades/${row.id}/cobranza/anular-cargo`, {
        cargoId: cargo.cargoId,
        motivo: 'Anulado desde detalle de ingresos'
      });
      setEditCargoId(null);
      setOkMsg('Cargo anulado.');
      await refrescarTodo();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'No se pudo anular.');
    } finally {
      setBusy(false);
    }
  };

  const limpiarDuplicados = async () => {
    setError('');
    setOkMsg('');
    setBusy(true);
    try {
      const r = await api.post(
        `/gastos-comunes/condominios/${cid}/unidades/${row.id}/cobranza/limpiar-duplicados`,
        {}
      );
      setOkMsg(r.data?.mensaje || 'Duplicados procesados.');
      await refrescarTodo();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'No se pudo limpiar duplicados.');
    } finally {
      setBusy(false);
    }
  };

  const revertirPago = async (pago) => {
    const ok = window.confirm(
      `¿Revertir el abono folio ${pago.folioNum || '—'} por ${clp(pago.importe)}? Libera el cargo para poder anular duplicados.`
    );
    if (!ok) return;
    setError('');
    setBusy(true);
    try {
      await api.post(`/gastos-comunes/condominios/${cid}/pagos/${pago.id || pago._id}/revertir`);
      setOkMsg('Pago revertido.');
      await refrescarTodo();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const aplicarAjuste = async (cargo) => {
    setError('');
    setBusy(true);
    try {
      const objetivo = Math.max(0, Math.round(Number(saldoObjetivo)));
      const delta = objetivo - cargo.pendiente;
      if (delta === 0) {
        setEditCargoId(null);
        return;
      }
      await api.post(`/gastos-comunes/condominios/${cid}/cargos/${cargo.cargoId}/ajustes`, {
        importe: delta,
        motivo: motivo || 'Ajuste manual desde ingresos',
        tipo: 'correccion'
      });
      setEditCargoId(null);
      setSaldoObjetivo('');
      setMotivo('');
      await refrescarTodo();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ingreso-overlay" role="presentation" onClick={onClose}>
      <aside
        className="ingreso-detalle-drawer"
        role="dialog"
        aria-labelledby="ingreso-detalle-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ingreso-detalle-head">
          <div>
            <p className="ingreso-cobro-kicker">Detalle de cuenta</p>
            <h2 id="ingreso-detalle-title" className="ingreso-cobro-title">
              Propiedad {row.codigoPropiedad}
            </h2>
            <p className="ingreso-cobro-sub">{row.titularNombre || 'Sin titular'}</p>
          </div>
          <button type="button" className="btn-ghost ingreso-cobro-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        {error && <div className="alert alert-error">{error}</div>}
        {okMsg && <div className="alert alert-success">{okMsg}</div>}

        <div className="ingreso-detalle-kpis">
          <div>
            <span>Total a pagar</span>
            <strong>{clp(row.totalPagar)}</strong>
          </div>
          <div>
            <span>Deuda vencida</span>
            <strong>{clp(row.deudaVencida)}</strong>
          </div>
          {periodoServicioLabel && (
            <div className="ingreso-kpi-wide">
              <span>Período en cobro</span>
              <strong>{periodoServicioLabel}</strong>
            </div>
          )}
        </div>

        {row.cuotaMensual != null && (
          <p className="detalle-meta">Cuota de referencia: {clp(row.cuotaMensual)}</p>
        )}

        <h3 className="card-title-sm">Cargos pendientes</h3>
        {hayDuplicados && (
          <div className="detalle-duplicado-bar">
            <p className="alert-warn detalle-duplicado-hint">
              Hay cargos duplicados del mismo mes. Pruebe primero el botón de limpieza automática (solo quita copias sin
              abono).
            </p>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={limpiarDuplicados}>
              Quitar duplicados sin abono
            </button>
          </div>
        )}
        {row.mesesImpagos?.length ? (
          <table className="ingreso-detalle-table">
            <thead>
              <tr>
                <th>Período</th>
                <th>Saldo</th>
                <th>Abono</th>
                <th>Vence</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {row.mesesImpagos.map((m) => (
                <React.Fragment key={m.cargoId}>
                  <tr className={m.vencido ? 'row-vencida' : ''}>
                    <td className="detalle-periodo">
                      {m.label}
                      {m.hayDuplicadoEnPeriodo && (
                        <span className="tag-duplicado" title="Varios cargos del mismo período">
                          dup.
                        </span>
                      )}
                    </td>
                    <td>
                      <strong>{clp(m.pendiente)}</strong>
                      {m.saldoCero && m.hayDuplicadoEnPeriodo && (
                        <span className="detalle-saldo-cero-hint"> saldo 0</span>
                      )}
                    </td>
                    <td className="detalle-abono">{m.aplicadoMonto > 0 ? clp(m.aplicadoMonto) : '—'}</td>
                    <td className="detalle-vence">
                      <span>{fmtFecha(m.vencimiento)}</span>
                      {m.vencido && <span className="tag-vencido">Vencido</span>}
                    </td>
                    <td className="detalle-acciones-cargo">
                      <button
                        type="button"
                        className="btn-link"
                        disabled={busy || m.saldoCero}
                        onClick={() => {
                          setEditCargoId(m.cargoId);
                          setSaldoObjetivo(String(m.pendiente));
                          setMotivo('');
                        }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-link btn-link-danger"
                        disabled={busy || tieneAbono(m)}
                        title={
                          tieneAbono(m)
                            ? 'Tiene abono aplicado; revierta el pago o use limpieza de duplicados'
                            : 'Anular cargo'
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          anularCargo(m);
                        }}
                      >
                        Anular
                      </button>
                    </td>
                  </tr>
                  {editCargoId === m.cargoId && (
                    <tr className="row-editing">
                      <td colSpan={5}>
                        <div className="ajuste-inline">
                          <label>
                            Saldo objetivo CLP
                            <input
                              type="number"
                              min="0"
                              value={saldoObjetivo}
                              onChange={(e) => setSaldoObjetivo(e.target.value)}
                            />
                          </label>
                          <label>
                            Motivo
                            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Opcional" />
                          </label>
                          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => aplicarAjuste(m)}>
                            Guardar
                          </button>
                          <button type="button" className="btn secondary btn-sm" onClick={() => setEditCargoId(null)}>
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty-hint">Sin saldos pendientes. Publique cargos o use Demo / Alinear períodos.</p>
        )}

        {pagos.length > 0 && (
          <>
            <h3 className="card-title-sm">Abonos registrados</h3>
            <table className="ingreso-detalle-table ingreso-detalle-pagos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Folio</th>
                  <th>Monto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagos.map((p) => (
                  <tr key={p.id || p._id}>
                    <td>{fmtFecha(p.fechaPago)}</td>
                    <td>{p.folioNum || '—'}</td>
                    <td>{clp(p.importe)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-link btn-link-danger"
                        disabled={busy}
                        onClick={() => revertirPago(p)}
                      >
                        Revertir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="ingreso-detalle-actions">
          <button type="button" className="btn btn-icon-text" disabled={!row.totalPagar} onClick={() => onRegistrarPago?.(row)}>
            <span className="btn-icon-glyph" aria-hidden>
              $
            </span>
            Pago
          </button>
          <button type="button" className="btn secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </aside>
    </div>
  );
}
